import React from 'react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,  } from 'recharts';
import Icon from '../../../components/AppIcon';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-xl shadow-xl p-3.5 text-sm min-w-[180px]">
      <p className="font-bold text-foreground mb-2.5 text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      {payload?.map((entry, i) => (
        <div key={i} className="flex items-center justify-between gap-4 mb-1.5">
          <span className="flex items-center gap-2 text-muted-foreground text-xs">
            <span
              className="w-2.5 h-2.5 rounded-sm inline-block flex-shrink-0"
              style={{ background: entry?.color }}
            />
            {entry?.name}
          </span>
          <span className="font-bold text-foreground text-xs">
            {entry?.value > 0
              ? `OMR ${Number(entry?.value)?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
              : '—'}
          </span>
        </div>
      ))}
    </div>
  );
};

const MonthlyTrendChart = ({ data = [], loading = false }) => {
  const chartData = MONTHS_SHORT?.map((month, idx) => {
    const found = data?.find(d => d?.month === idx + 1);
    return {
      month,
      Revenue: found?.revenue || 0,
      Profit: found?.profit || 0,
    };
  });

  const hasData = chartData?.some(d => d?.Revenue > 0 || d?.Profit > 0);

  if (loading) {
    return (
      <div className="h-72 bg-muted/20 rounded-xl animate-pulse flex items-center justify-center">
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Icon name="Loader2" size={16} className="animate-spin" />
          Loading chart...
        </div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="h-72 flex flex-col items-center justify-center text-muted-foreground gap-3">
        <div className="w-14 h-14 rounded-full bg-muted/20 flex items-center justify-center">
          <Icon name="BarChart3" size={24} className="opacity-30" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium">No Data Available</p>
          <p className="text-xs text-muted-foreground/60 mt-0.5">No revenue or profit data for this year</p>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} opacity={0.5} />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
          axisLine={false}
          tickLine={false}
          tickFormatter={v => v >= 1000 ? `${(v / 1000)?.toFixed(0)}K` : v}
          width={44}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.3 }} />
        <Legend
          wrapperStyle={{ fontSize: 12, paddingTop: 12 }}
          formatter={value => <span style={{ color: 'hsl(var(--foreground))', fontSize: 12 }}>{value}</span>}
        />
        <Bar
          dataKey="Revenue"
          fill="#3b82f6"
          fillOpacity={0.85}
          radius={[5, 5, 0, 0]}
          maxBarSize={36}
        />
        <Line
          dataKey="Profit"
          type="monotone"
          stroke="#10b981"
          strokeWidth={2.5}
          dot={{ r: 3.5, fill: '#10b981', strokeWidth: 0 }}
          activeDot={{ r: 6, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default MonthlyTrendChart;
